import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus, Trash2, Loader2, FileText, Send, Download, CreditCard, RotateCcw, Pencil, Search,
  Receipt, AlertCircle, Wallet, Clock,
} from 'lucide-react';
import { format, parseISO, startOfMonth, subMonths, startOfWeek, subWeeks, isValid } from 'date-fns';
import { toast } from 'sonner';

import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  useInvoices,
  useInvoiceDetail,
  useInvoiceLinkOptions,
  useSaveInvoice,
  useSendInvoice,
  useDownloadInvoice,
  useCreateRefund,
  INVOICE_STATUSES,
  type InvoiceStatus,
} from '@/hooks/useInvoices';
import { useCreateCheckoutSession } from '@/hooks/useCreateCheckoutSession';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { buildPaypalMeUrl } from '@/lib/paypal';
import { getCurrencySetting, CURRENCIES, type CurrencyCode } from '@/lib/currency';
import { customerMatchesSource } from '@/lib/customerProfiles';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AdminPage,
  EmptyState,
  FilterChip,
  PageHeader,
  Panel,
  PersonAvatar,
  StatCard,
  StatusChip,
  Toolbar,
  ToolbarSpacer,
  adminButton,
  useAdminPageTitle,
  type AdminTone,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const NONE = 'none';

const lineItemSchema = z.object({
  description: z.string().trim().min(1, 'Description is required'),
  quantity: z.coerce.number({ invalid_type_error: 'Required' }).positive('Must be greater than 0'),
  unit_amount: z.coerce.number({ invalid_type_error: 'Required' }).min(0, 'Cannot be negative'),
  service_id: z.string().optional(),
});

const invoiceFormSchema = z.object({
  customer_id: z.string().optional(),
  booking_id: z.string().optional(),
  invoice_number: z.string().optional(),
  status: z.enum(['draft', 'sent', 'partially_paid', 'paid', 'overdue', 'void', 'cancelled', 'refunded']),
  currency_code: z.enum(['GBP', 'EUR', 'USD']),
  tax_amount: z.coerce.number({ invalid_type_error: 'Required' }).min(0, 'Cannot be negative'),
  due_date: z.string().optional(),
  notes: z.string().optional(),
  line_items: z.array(lineItemSchema).min(1, 'Add at least one line item'),
});

type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

const emptyFormValues = (currency: CurrencyCode): InvoiceFormValues => ({
  customer_id: NONE,
  booking_id: NONE,
  invoice_number: '',
  status: 'draft',
  currency_code: currency,
  tax_amount: 0,
  due_date: '',
  notes: '',
  line_items: [{ description: '', quantity: 1, unit_amount: 0, service_id: NONE }],
});

const STATUS_TONE: Record<InvoiceStatus, AdminTone> = {
  draft: 'grey',
  sent: 'blue',
  partially_paid: 'amber',
  paid: 'green',
  overdue: 'rose',
  void: 'grey',
  cancelled: 'grey',
  refunded: 'violet',
};

/** Statuses that always get a filter chip; the rest appear only when present. */
const PRIMARY_FILTERS: InvoiceStatus[] = ['draft', 'sent', 'partially_paid', 'paid', 'overdue'];
const OPEN_STATUSES: InvoiceStatus[] = ['sent', 'partially_paid', 'overdue'];

const VIOLET = 'hsl(var(--adm-violet))';
const ROSE = 'hsl(352 75% 60%)';
const MINT = 'hsl(172 46% 48%)';
const SUN = 'hsl(42 95% 52%)';

const safeDate = (value: string | null | undefined) => {
  if (!value) return null;
  const d = parseISO(value);
  return isValid(d) ? d : null;
};

const formatShortDate = (value: string | null | undefined) => {
  const d = safeDate(value);
  return d ? format(d, 'd MMM yyyy') : '—';
};

const statusLabel = (status: InvoiceStatus) =>
  INVOICE_STATUSES.find((s) => s.value === status)?.label ?? status;

const formatAmount = (amount: number | string | null | undefined, currency: CurrencyCode) => {
  const value = Number(amount ?? 0);
  return `${getCurrencySetting(currency).symbol}${value.toFixed(2)}`;
};

/** KPI money: whole units with thousands separators (e.g. £1,240). */
const formatKpi = (amount: number, currency: CurrencyCode) =>
  `${getCurrencySetting(currency).symbol}${Math.round(amount).toLocaleString('en-GB')}`;

const InvoicesAdmin = () => {
  useAdminPageTitle('Invoices');
  const isMobile = useIsMobile();
  const { orgId, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { data: invoices = [], isLoading } = useInvoices(orgId);
  const { data: linkOptions } = useInvoiceLinkOptions(orgId);
  const saveInvoice = useSaveInvoice();
  const sendInvoice = useSendInvoice();
  const downloadInvoice = useDownloadInvoice();
  const createCheckoutSession = useCreateCheckoutSession();
  const { data: orgSettings } = useOrgSettings(orgId);
  const paypal = orgSettings?.paymentMethods.paypal;
  const paypalHandle = paypal?.enabled && paypal.available ? paypal.paypal_me : undefined;

  const createRefund = useCreateRefund();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  // Client-side summaries over the already-loaded list (no extra queries).
  const kpiCurrency: CurrencyCode = invoices[0]?.currency_code ?? 'GBP';
  const summary = useMemo(() => {
    const now = new Date();
    const thisMonth = startOfMonth(now);
    const lastMonth = startOfMonth(subMonths(now, 1));
    const balance = (i: (typeof invoices)[number]) =>
      Math.max(0, Number(i.total_amount) - Number(i.amount_paid));
    const open = invoices.filter((i) => OPEN_STATUSES.includes(i.status));
    const overdue = invoices.filter((i) => i.status === 'overdue');
    const live = invoices.filter((i) => !['void', 'cancelled'].includes(i.status));
    const createdIn = (from: Date, to: Date | null) =>
      live.filter((i) => {
        const d = safeDate(i.created_at);
        return d ? d >= from && (!to || d < to) : false;
      });
    const sum = (rows: typeof invoices, pick: (i: (typeof invoices)[number]) => number) =>
      rows.reduce((t, i) => t + pick(i), 0);
    const invoicedThisMonth = sum(createdIn(thisMonth, null), (i) => Number(i.total_amount));
    const invoicedLastMonth = sum(createdIn(lastMonth, thisMonth), (i) => Number(i.total_amount));
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekly = Array.from({ length: 8 }, (_, idx) => {
      const from = subWeeks(weekStart, 7 - idx);
      const to = subWeeks(weekStart, 6 - idx);
      return sum(createdIn(from, to), (i) => Number(i.total_amount));
    });
    const counts = invoices.reduce<Record<string, number>>((acc, i) => {
      acc[i.status] = (acc[i.status] ?? 0) + 1;
      return acc;
    }, {});
    return {
      outstanding: sum(open, balance),
      openCount: open.length,
      overdue: sum(overdue, balance),
      overdueCount: overdue.length,
      collected: sum(invoices, (i) => Math.max(0, Number(i.amount_paid) - Number(i.amount_refunded))),
      paidCount: invoices.filter((i) => Number(i.amount_paid) > 0).length,
      invoicedThisMonth,
      invoicedDelta: invoicedLastMonth > 0 ? ((invoicedThisMonth - invoicedLastMonth) / invoicedLastMonth) * 100 : null,
      weekly,
      counts,
    };
  }, [invoices]);

  const filterChips = useMemo(
    () => INVOICE_STATUSES.filter((s) => PRIMARY_FILTERS.includes(s.value) || (summary.counts[s.value] ?? 0) > 0),
    [summary.counts],
  );

  const visibleInvoices = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter((i) => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (!q) return true;
      return [i.invoice_number, i.customer_name].some((v) => v?.toLowerCase().includes(q));
    });
  }, [invoices, statusFilter, search]);
  const { data: editingDetail } = useInvoiceDetail(editingId);

  // Refund dialog state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundInvoiceId, setRefundInvoiceId] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState<'requested_by_customer' | 'duplicate' | 'fraudulent'>('requested_by_customer');

  const openRefundDialog = (invoice: { id: string; amount_paid: number; amount_refunded: number; currency_code: CurrencyCode }) => {
    if (!assertCanWrite()) return;
    const refundable = Math.max(0, invoice.amount_paid - invoice.amount_refunded);
    setRefundInvoiceId(invoice.id);
    setRefundAmount(refundable.toFixed(2));
    setRefundReason('requested_by_customer');
    setRefundDialogOpen(true);
  };

  const handleRefundSubmit = async () => {
    if (!assertCanWrite()) return;
    if (!orgId || !refundInvoiceId) return;
    const amount = parseFloat(refundAmount);
    try {
      await createRefund.mutateAsync({
        organizationId: orgId,
        invoiceId: refundInvoiceId,
        amount: Number.isFinite(amount) && amount > 0 ? amount : undefined,
        reason: refundReason,
      });
      toast.success('Refund issued successfully');
      setRefundDialogOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not issue refund');
    }
  };

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: emptyFormValues('GBP'),
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'line_items' });

  // Live totals from the current form state.
  const watchedLineItems = form.watch('line_items');
  const watchedTax = form.watch('tax_amount');
  const watchedCurrency = form.watch('currency_code') as CurrencyCode;
  const watchedCustomerId = form.watch('customer_id');
  const watchedBookingId = form.watch('booking_id');
  const invoiceCustomers = useMemo(() => linkOptions?.customers ?? [], [linkOptions?.customers]);
  const invoiceJobs = useMemo(() => linkOptions?.jobs ?? [], [linkOptions?.jobs]);
  const selectedCustomer = watchedCustomerId && watchedCustomerId !== NONE
    ? invoiceCustomers.find((customer) => customer.id === watchedCustomerId)
    : null;
  const selectedJob = watchedBookingId && watchedBookingId !== NONE
    ? invoiceJobs.find((job) => job.id === watchedBookingId)
    : null;
  const jobsForSelectedCustomer = selectedCustomer
    ? invoiceJobs.filter((job) => customerMatchesSource(selectedCustomer, {
        customer_id: job.customer_id,
        email: job.client_email,
        phone: job.client_phone,
      }))
    : invoiceJobs;
  const totals = useMemo(() => {
    const subtotal = (watchedLineItems ?? []).reduce(
      (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unit_amount) || 0),
      0
    );
    const tax = Number(watchedTax) || 0;
    return { subtotal, tax, total: subtotal + tax };
  }, [watchedLineItems, watchedTax]);

  useEffect(() => {
    if (!dialogOpen || editingId) return;

    if (selectedCustomer) {
      const currentJobMatchesCustomer = selectedJob
        ? customerMatchesSource(selectedCustomer, {
            customer_id: selectedJob.customer_id,
            email: selectedJob.client_email,
            phone: selectedJob.client_phone,
          })
        : false;

      if (!currentJobMatchesCustomer) {
        form.setValue('booking_id', jobsForSelectedCustomer.length === 1 ? jobsForSelectedCustomer[0].id : NONE, {
          shouldDirty: true,
        });
      }
      return;
    }

    if (selectedJob) {
      const matchingCustomer = invoiceCustomers.find((customer) => customerMatchesSource(customer, {
        customer_id: selectedJob.customer_id,
        email: selectedJob.client_email,
        phone: selectedJob.client_phone,
      }));
      if (matchingCustomer) {
        form.setValue('customer_id', matchingCustomer.id, { shouldDirty: true });
      }
    }
  }, [dialogOpen, editingId, form, invoiceCustomers, jobsForSelectedCustomer, selectedCustomer, selectedJob]);

  useEffect(() => {
    if (!dialogOpen || editingId || !selectedJob) return;

    const description = selectedJob.service_name || selectedJob.title;
    form.setValue('line_items.0.description', description, { shouldDirty: true });
    form.setValue('line_items.0.quantity', 1, { shouldDirty: true });
    form.setValue('line_items.0.service_id', selectedJob.service_id ?? NONE, { shouldDirty: true });
    if (selectedJob.service_price != null) {
      form.setValue('line_items.0.unit_amount', selectedJob.service_price, { shouldDirty: true });
    }
  }, [dialogOpen, editingId, form, selectedJob]);

  // Populate the form when an existing invoice is loaded for editing.
  useEffect(() => {
    if (!editingId || !editingDetail) return;
    form.reset({
      customer_id: editingDetail.customer_id ?? NONE,
      booking_id: editingDetail.booking_id ?? NONE,
      invoice_number: editingDetail.invoice_number ?? '',
      status: editingDetail.status,
      currency_code: editingDetail.currency_code,
      tax_amount: Number(editingDetail.tax_amount) || 0,
      due_date: editingDetail.due_date ?? '',
      notes: editingDetail.notes ?? '',
      line_items: editingDetail.line_items.length
        ? editingDetail.line_items.map((li) => ({
            description: li.description,
            quantity: Number(li.quantity) || 1,
            unit_amount: Number(li.unit_amount) || 0,
            service_id: li.service_id ?? NONE,
          }))
        : [{ description: '', quantity: 1, unit_amount: 0, service_id: NONE }],
    });
  }, [editingId, editingDetail, form]);

  const openCreate = () => {
    if (!assertCanWrite()) return;
    setEditingId(null);
    form.reset(emptyFormValues('GBP'));
    setDialogOpen(true);
  };

  const openEdit = (id: string) => {
    if (!assertCanWrite()) return;
    setEditingId(id);
    setDialogOpen(true);
  };

  // `?invoice=<id>` (e.g. "Convert to invoice" from a quote) opens that invoice
  // once, then drops the param so a refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const requested = searchParams.get('invoice');
    if (!requested) return;
    if (canWrite) {
      setEditingId(requested);
      setDialogOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('invoice');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canWrite]);

  const handleSend = async (id: string) => {
    if (!assertCanWrite()) return;
    if (!orgId) return;
    try {
      const result = await sendInvoice.mutateAsync({ invoiceId: id, organizationId: orgId });
      if (result.skipped) {
        toast.info('No customer email on this invoice — nothing was sent.');
      } else {
        toast.success(
          result.recipient ? `Invoice sent to ${result.recipient}` : 'Invoice sent to customer'
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not send invoice');
    }
  };

  const handleDownload = async (id: string) => {
    if (!orgId) return;
    try {
      await downloadInvoice.mutateAsync({ invoiceId: id, organizationId: orgId });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not download invoice');
    }
  };

  const handlePaymentLink = async (id: string, currencyCode: CurrencyCode = 'GBP') => {
    if (!assertCanWrite()) return;
    if (!orgId) return;
    try {
      const result = await createCheckoutSession.mutateAsync({ invoiceId: id, organizationId: orgId });
      const extResult = result as typeof result & { chargeAmount?: number; chargeKind?: string; currencyCode?: string };
      if (result.configured && result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
        if (extResult.chargeKind === 'deposit' && extResult.chargeAmount != null) {
          const symbol = getCurrencySetting((extResult.currencyCode ?? currencyCode) as CurrencyCode).symbol;
          toast.success(`Charging deposit of ${symbol}${extResult.chargeAmount.toFixed(2)}`);
        } else {
          toast.success('Stripe Checkout opened in a new tab');
        }
      } else if (!result.configured && result.paymentLink) {
        window.open(result.paymentLink, '_blank', 'noopener,noreferrer');
        toast.info('Opened saved Stripe Payment Link');
      } else {
        toast.info('No online payment method is configured for this organisation');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not open payment link');
    }
  };

  // PayPal.me link pre-filled with the amount still due (null when nothing is owed).
  const paypalUrl = (invoice: { status: InvoiceStatus; total_amount: number; amount_paid: number; currency_code: CurrencyCode }) => {
    if (!paypalHandle || ['draft', 'paid', 'void', 'cancelled', 'refunded'].includes(invoice.status)) return null;
    const due = Math.round((Number(invoice.total_amount) - Number(invoice.amount_paid)) * 100) / 100;
    return due > 0 ? buildPaypalMeUrl(paypalHandle, due, invoice.currency_code) : null;
  };

  const isSending = (id: string) =>
    sendInvoice.isPending && sendInvoice.variables?.invoiceId === id;
  const isDownloading = (id: string) =>
    downloadInvoice.isPending && downloadInvoice.variables?.invoiceId === id;
  const isOpeningPayment = (id: string) =>
    createCheckoutSession.isPending && createCheckoutSession.variables?.invoiceId === id;
  const isRefunding = (id: string) =>
    createRefund.isPending && createRefund.variables?.invoiceId === id;

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingId(null);
      form.reset(emptyFormValues('GBP'));
    }
  };

  const onSubmit = async (values: InvoiceFormValues) => {
    if (!assertCanWrite()) return;
    if (!orgId) return;

    const customer = values.customer_id && values.customer_id !== NONE
      ? linkOptions?.customers.find((c) => c.id === values.customer_id)
      : undefined;

    try {
      await saveInvoice.mutateAsync({
        id: editingId,
        organization_id: orgId,
        customer_id: customer?.customer_id ?? null,
        booking_id: values.booking_id && values.booking_id !== NONE ? values.booking_id : null,
        customer_name: customer?.name ?? null,
        customer_email: customer?.email ?? null,
        invoice_number: values.invoice_number?.trim() ? values.invoice_number.trim() : null,
        status: values.status,
        currency_code: values.currency_code,
        tax_amount: values.tax_amount,
        due_date: values.due_date?.trim() ? values.due_date : null,
        notes: values.notes?.trim() ? values.notes.trim() : null,
        line_items: values.line_items.map((li) => ({
          description: li.description.trim(),
          quantity: li.quantity,
          unit_amount: li.unit_amount,
          service_id: li.service_id && li.service_id !== NONE ? li.service_id : null,
        })),
      });
      toast.success(editingId ? 'Invoice updated' : 'Invoice created');
      handleDialogChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save invoice');
    }
  };

  const rowAction = (opts: {
    label: string;
    title: string;
    icon: ReactNode;
    busy?: boolean;
    disabled?: boolean;
    onClick: () => void;
  }) => (
    <Button
      variant="ghost"
      size="sm"
      className={adminButton.iconGhost}
      aria-label={opts.label}
      title={opts.title}
      disabled={opts.disabled}
      onClick={opts.onClick}
    >
      {opts.busy ? <Loader2 className="animate-spin" /> : opts.icon}
    </Button>
  );

  return (
    <AdminPage>
      <PageHeader
        description="Create and manage invoices for your customers and jobs."
        actions={
          <Button size="sm" onClick={openCreate} disabled={!canWrite} className={adminButton.primary}>
            <Plus />
            New invoice
          </Button>
        }
      />

      {invoices.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          <StatCard
            label="Invoiced this month"
            icon={<Receipt />}
            value={formatKpi(summary.invoicedThisMonth, kpiCurrency)}
            delta={summary.invoicedDelta}
            deltaLabel="vs last month"
            hint={summary.invoicedDelta === null ? 'Last 8 weeks' : undefined}
            trend={isMobile ? undefined : summary.weekly}
            trendType="bar"
            color={VIOLET}
          />
          <StatCard
            label="Outstanding"
            icon={<Clock />}
            value={formatKpi(summary.outstanding, kpiCurrency)}
            hint={`${summary.openCount} unpaid invoice${summary.openCount === 1 ? '' : 's'}`}
            color={SUN}
          />
          <StatCard
            label="Overdue"
            icon={<AlertCircle />}
            value={formatKpi(summary.overdue, kpiCurrency)}
            hint={summary.overdueCount ? `${summary.overdueCount} past due date` : 'Nothing overdue'}
            color={ROSE}
          />
          <StatCard
            label="Collected"
            icon={<Wallet />}
            value={formatKpi(summary.collected, kpiCurrency)}
            hint={`Across ${summary.paidCount} paid invoice${summary.paidCount === 1 ? '' : 's'}`}
            color={MINT}
          />
        </div>
      )}

      <Panel
        title="All invoices"
        description={`${invoices.length} invoice${invoices.length === 1 ? '' : 's'} in this organisation.`}
        padded={false}
      >
        {invoices.length > 0 && (
          <Toolbar className="border-b border-border px-4 pb-3 pt-3 md:px-5">
            <div
              className="adm-scroll -mx-4 flex w-[calc(100%+2rem)] items-center gap-1.5 overflow-x-auto px-4 pb-0.5 sm:mx-0 sm:w-auto sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0"
              role="group"
              aria-label="Filter by status"
            >
              <FilterChip active={statusFilter === 'all'} count={invoices.length} onClick={() => setStatusFilter('all')}>
                All
              </FilterChip>
              {filterChips.map((s) => (
                <FilterChip
                  key={s.value}
                  active={statusFilter === s.value}
                  count={summary.counts[s.value] ?? 0}
                  onClick={() => setStatusFilter(s.value)}
                >
                  {s.label}
                </FilterChip>
              ))}
            </div>
            <ToolbarSpacer />
            <div className="adm-search h-9 w-full sm:w-[240px]">
              <Search className="h-4 w-4 shrink-0" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search number or customer"
                aria-label="Search invoices"
              />
            </div>
          </Toolbar>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={<FileText />}
            title="No invoices yet."
            description="Create your first invoice to get started."
          />
        ) : visibleInvoices.length === 0 ? (
          <EmptyState
            compact
            icon={<Search />}
            title="No matching invoices"
            description="Try a different status or search term."
          />
        ) : (
          <Table className="adm-table min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Due date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only md:not-sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleInvoices.map((invoice) => {
                const refundable = Number(invoice.amount_paid) - Number(invoice.amount_refunded) > 0;
                const number = invoice.invoice_number || '';
                return (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-muted-foreground sm:flex">
                          <FileText className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="adm-num whitespace-nowrap font-semibold text-foreground">
                            {invoice.invoice_number || 'Draft'}
                          </p>
                          <p className="whitespace-nowrap text-[11.5px] text-muted-foreground">
                            Issued {formatShortDate(invoice.created_at)}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {invoice.customer_name ? (
                        <div className="flex min-w-0 items-center gap-2">
                          <PersonAvatar name={invoice.customer_name} size={26} />
                          <span className="truncate font-medium text-foreground">{invoice.customer_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip tone={STATUS_TONE[invoice.status] ?? 'grey'}>{statusLabel(invoice.status)}</StatusChip>
                    </TableCell>
                    <TableCell
                      className={cn(
                        'hidden whitespace-nowrap md:table-cell',
                        invoice.status === 'overdue' ? 'font-medium text-destructive' : 'text-muted-foreground',
                      )}
                    >
                      {invoice.due_date ? formatShortDate(invoice.due_date) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="adm-num font-semibold text-foreground">
                        {formatAmount(invoice.total_amount, invoice.currency_code)}
                      </div>
                      {Number(invoice.amount_refunded) > 0 && (
                        <div className="text-[11.5px] text-muted-foreground">
                          Refunded {formatAmount(invoice.amount_refunded, invoice.currency_code)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {rowAction({
                          label: `Download invoice ${number}`.trim(),
                          title: 'Download',
                          icon: <Download />,
                          busy: isDownloading(invoice.id),
                          disabled: isDownloading(invoice.id),
                          onClick: () => handleDownload(invoice.id),
                        })}
                        {rowAction({
                          label: `Send invoice ${number}`.trim(),
                          title: 'Send',
                          icon: <Send />,
                          busy: isSending(invoice.id),
                          disabled: !canWrite || isSending(invoice.id),
                          onClick: () => handleSend(invoice.id),
                        })}
                        {rowAction({
                          label: `Payment link for invoice ${number}`.trim(),
                          title: 'Payment link',
                          icon: <CreditCard />,
                          busy: isOpeningPayment(invoice.id),
                          disabled: !canWrite || isOpeningPayment(invoice.id),
                          onClick: () => handlePaymentLink(invoice.id, invoice.currency_code),
                        })}
                        {paypalUrl(invoice) && rowAction({
                          label: `Pay with PayPal for invoice ${number}`.trim(),
                          title: 'Pay with PayPal',
                          icon: <Wallet />,
                          onClick: () => window.open(paypalUrl(invoice)!, '_blank', 'noopener,noreferrer'),
                        })}
                        {rowAction({
                          label: 'Edit',
                          title: 'Edit',
                          icon: <Pencil />,
                          disabled: !canWrite,
                          onClick: () => openEdit(invoice.id),
                        })}
                        {refundable ? (
                          rowAction({
                            label: `Refund invoice ${number}`.trim(),
                            title: 'Refund',
                            icon: <RotateCcw />,
                            busy: isRefunding(invoice.id),
                            disabled: !canWrite || isRefunding(invoice.id),
                            onClick: () => openRefundDialog(invoice),
                          })
                        ) : (
                          <span className="inline-block h-8 w-8" aria-hidden="true" />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl p-5 sm:p-6">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-[17px] font-semibold tracking-tight">{editingId ? 'Edit invoice' : 'New invoice'}</DialogTitle>
            <DialogDescription>
              Link a customer or job, add line items, and set the status.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="adm-form space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="customer_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No customer" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>No customer</SelectItem>
                          {linkOptions?.customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>
                              {customer.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="booking_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Linked job</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No job" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NONE}>No job</SelectItem>
                          {jobsForSelectedCustomer.map((job) => (
                            <SelectItem key={job.id} value={job.id}>
                              {job.title}
                              {job.scheduled_date ? ` · ${job.scheduled_date}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="invoice_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice number</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. INV-001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {INVOICE_STATUSES.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCIES.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.code} ({currency.symbol})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="due_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2.5">
                <div className="flex items-center justify-between">
                  <FormLabel>Line items</FormLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={adminButton.outline}
                    onClick={() => append({ description: '', quantity: 1, unit_amount: 0, service_id: NONE })}
                  >
                    <Plus />
                    Add line
                  </Button>
                </div>
                <div className="hidden grid-cols-12 gap-2 px-3 sm:grid" aria-hidden="true">
                  <span className="adm-label col-span-6">Description</span>
                  <span className="adm-label col-span-2">Qty</span>
                  <span className="adm-label col-span-3">Unit price</span>
                </div>

                {fields.map((fieldRow, index) => (
                  <div
                    key={fieldRow.id}
                    className="grid grid-cols-12 items-start gap-2 rounded-xl border border-border bg-muted/30 p-2.5"
                  >
                    <div className="col-span-12 sm:col-span-6">
                      <FormField
                        control={form.control}
                        name={`line_items.${index}.description`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                placeholder="Item description"
                                aria-label={`Line ${index + 1} description`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <FormField
                        control={form.control}
                        name={`line_items.${index}.quantity`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Qty"
                                aria-label={`Line ${index + 1} quantity`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-5 sm:col-span-3">
                      <FormField
                        control={form.control}
                        name={`line_items.${index}.unit_amount`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Unit price"
                                aria-label={`Line ${index + 1} unit price`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-3 flex justify-end pt-0.5 sm:col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={cn(adminButton.iconGhost, 'hover:text-destructive')}
                        aria-label={`Remove line ${index + 1}`}
                        disabled={fields.length === 1}
                        onClick={() => remove(index)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                ))}
                {form.formState.errors.line_items?.root && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.line_items.root.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea rows={3} placeholder="Optional notes for the customer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-[13px]">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="adm-num">{formatAmount(totals.subtotal, watchedCurrency)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Tax</span>
                    <FormField
                      control={form.control}
                      name="tax_amount"
                      render={({ field }) => (
                        <FormItem className="w-28">
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="h-8 text-right"
                              aria-label="Tax amount"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex justify-between border-t border-border pt-2 text-[14px] font-semibold">
                    <span>Total</span>
                    <span className="adm-num">{formatAmount(totals.total, watchedCurrency)}</span>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 border-t border-border pt-4 sm:gap-2">
                <Button type="button" variant="ghost" size="sm" className={adminButton.outline} onClick={() => handleDialogChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" className={adminButton.primary} disabled={!canWrite || saveInvoice.isPending}>
                  {saveInvoice.isPending && <Loader2 className="animate-spin" />}
                  {editingId ? 'Save changes' : 'Create invoice'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={(open) => { setRefundDialogOpen(open); }}>
        <DialogContent className="max-w-sm rounded-2xl p-5 sm:p-6">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-[17px] font-semibold tracking-tight">Issue refund</DialogTitle>
            <DialogDescription>
              Enter the amount to refund and select a reason. Leave blank to refund the full refundable amount.
            </DialogDescription>
          </DialogHeader>
          <div className="adm-form space-y-4 py-2">
            <div className="space-y-1.5">
              <label htmlFor="refund-amount">
                Refund amount
              </label>
              <Input
                id="refund-amount"
                type="number"
                min="0"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="refund-reason">
                Reason
              </label>
              <Select
                value={refundReason}
                onValueChange={(v) => setRefundReason(v as typeof refundReason)}
              >
                <SelectTrigger id="refund-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                  <SelectItem value="duplicate">Duplicate</SelectItem>
                  <SelectItem value="fraudulent">Fraudulent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" size="sm" className={adminButton.outline} onClick={() => setRefundDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className={adminButton.primary}
              onClick={handleRefundSubmit}
              disabled={!canWrite || createRefund.isPending}
            >
              {createRefund.isPending && <Loader2 className="animate-spin" />}
              Issue refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

export default InvoicesAdmin;
